import { supabase } from '../lib/supabase';
import { triggerMakeWebhook } from './webhook';
import { ReportError } from '../utils/error';
import { logger } from '../utils/logger';

export interface CreateReportData {
  resumeFile: File;
  linkedinUrl?: string;
  jobUrl?: string;
  interviewerInfo?: {
    fullName: string;
    jobTitle: string;
  };
  jobDescription?: string;
  companyWebsite?: string;
}

export async function createReport(data: CreateReportData) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    logger.error('User not authenticated');
    throw new ReportError('User not authenticated');
  }

  try {
    // Upload resume file
    const fileExt = data.resumeFile.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;
    
    logger.info('Uploading resume file', { fileName });
    const { error: uploadError } = await supabase.storage
      .from('resumes')
      .upload(fileName, data.resumeFile);

    if (uploadError) {
      logger.error('Failed to upload resume', uploadError, { fileName });
      throw new ReportError('Failed to upload resume', uploadError);
    }

    // Create report record
    logger.info('Creating report with data', { data });
    const { error: reportError, data: report } = await supabase
      .from('reports')
      .insert({
        user_id: user.id,
        linkedin_url: data.linkedinUrl || '',
        job_url: data.jobUrl || '',
        resume_path: fileName,
        interviewer_name: data.interviewerInfo?.fullName || null,
        interviewer_title: data.interviewerInfo?.jobTitle || null,
        company_website: data.companyWebsite,
        job_description: data.jobDescription || null,
        status: 'pending'
      })
      .select()
      .single();

    if (reportError || !report) {
      logger.error('Failed to create report record', reportError);
      throw new ReportError('Failed to create report record', reportError);
    }

    // Trigger webhook
    logger.info('Report created, triggering webhook', { reportId: report.id });
    await triggerMakeWebhook(report.id);

    return report;
  } catch (error) {
    logger.error('Report creation failed', error);
    throw error;
  }
}